import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import {
  CreateOrderRequest,
  CreateOrderResult,
  DeliverabilityResult,
  DeviceSpecInput,
  PackingResult,
  QuoteRequest,
  QuoteResult,
  PatientShippingCheck,
  RequoteResult,
  ShipmentOrder,
  ShipmentOrderDetail,
  ShippablePatientPage,
  ShippingContact,
  ShippingPreferences,
  ShippingRefusal,
} from './shipping.type';

/**
 * Booking a device trial, and watching it afterwards.
 *
 * Three steps in the shipping flow, deliberately separate calls:
 *
 *   pack   → local only. No carrier is contacted and nothing is charged.
 *   quote  → asks the carrier for a price. Still nothing charged.
 *   order  → 💰 buys the labels. This one spends money.
 *
 * Splitting them is what lets the dialog show a packing and a price before
 * anyone commits, and it is why `pack` and `quote` can be called freely as an
 * admin edits the form while `order` is called exactly once, from one button.
 */
@Injectable({
  providedIn: 'root',
})
export class ShippingService extends ApiService {
  /**
   * Devices → suggested boxes.
   *
   * Returns `problems` rather than failing when a device type has no packing
   * spec: the dialog shows them next to the offending type, because the fix is a
   * settings form somebody can go and fill in.
   */
  public pack(deviceIds: number[], specs?: DeviceSpecInput[]): Observable<PackingResult> {
    return this.request({
      path: 'integrations/easyship/pack',
      method: 'POST',
      body: { deviceIds, specs },
    });
  }

  /**
   * Every courier service for this shipment, compared. Nothing is charged and
   * nothing is reserved — the prices are a quote, sealed so the purchase can
   * prove which one was agreed.
   */
  public quote(payload: QuoteRequest): Observable<QuoteResult | ShippingRefusal> {
    return this.request({
      path: 'integrations/easyship/quote',
      method: 'POST',
      body: payload,
    });
  }

  /**
   * 💰 Confirm and pay, with the courier the admin chose.
   *
   * Safe to hit twice: the request carries a key made once per attempt, so a
   * second request returns the first one's trial rather than buying a second
   * set of labels. The dialog still disables the button, because a user should
   * not have to rely on that.
   */
  public createOrder(payload: CreateOrderRequest): Observable<CreateOrderResult | ShippingRefusal> {
    return this.request({
      path: 'integrations/easyship/orders',
      method: 'POST',
      body: payload,
    });
  }

  /** The comparison again for an existing trial, beside what was booked. Free. */
  public requote(orderId: number): Observable<RequoteResult | ShippingRefusal> {
    return this.request({
      path: `integrations/easyship/orders/${orderId}/requote`,
      method: 'POST',
      body: {},
    });
  }

  /** 💰 Void a trial's labels before the parcels move, so Easyship refunds them. */
  public cancelOrder(
    orderId: number
  ): Observable<(ShipmentOrderDetail & { ok: true; messages: string[] }) | ShippingRefusal> {
    return this.request({
      path: `integrations/easyship/orders/${orderId}/cancel`,
      method: 'POST',
      body: {},
    });
  }

  public listOrders(): Observable<{ items: ShipmentOrder[] }> {
    return this.request({
      path: 'integrations/easyship/orders',
      method: 'GET',
    });
  }

  /** One trial: its legs, its labels, and the full carrier timeline. */
  /**
   * The shipment's devices are back at the lab: the parcels are recorded as
   * delivered back, exactly as the courier's delivery scan would record them.
   * No `parcelIds` receives every parcel still out.
   */
  public markReceived(
    orderId: number,
    parcelIds?: number[]
  ): Observable<(ShipmentOrderDetail & { ok: true; received: number }) | ShippingRefusal> {
    return this.request({
      path: `integrations/easyship/orders/${orderId}/received`,
      method: 'POST',
      body: parcelIds?.length ? { parcelIds } : {},
    });
  }

  public getOrder(id: number): Observable<ShipmentOrderDetail> {
    return this.request({
      path: `integrations/easyship/orders/${id}`,
      method: 'GET',
    });
  }

  // ───────────────────────────────────────────── address deliverability ──

  /**
   * Can Easyship deliver to this address and bring the device back? Two free
   * Easyship price requests at most — none when the same postcode was checked
   * recently.
   */
  public checkAddress(address: ShippingContact, originId?: number | null): Observable<DeliverabilityResult> {
    return this.request({
      path: 'integrations/easyship/deliverability',
      method: 'POST',
      body: { address, originId: originId ?? undefined },
    });
  }

  /**
   * Patients for the ship dialog, a page at a time, with what is known about
   * each address as seen from `originId` (the default site when left out).
   * Asks Easyship nothing.
   */
  public listShippablePatients(options: {
    q: string;
    onlyDeliverable: boolean;
    originId?: number | null;
    offset?: number;
  }): Observable<ShippablePatientPage> {
    const query: Record<string, string> = {
      q: options.q ?? '',
      onlyDeliverable: String(options.onlyDeliverable),
      offset: String(options.offset ?? 0),
    };
    if (options.originId) query['originId'] = String(options.originId);
    return this.request({ path: 'integrations/easyship/patients', method: 'GET', query });
  }

  /** One patient, checked from `originId` now if the last answer is old. */
  public checkPatient(patientId: number, originId?: number | null): Observable<PatientShippingCheck> {
    return this.request({
      path: `integrations/easyship/patients/${patientId}/deliverability`,
      method: 'POST',
      body: { originId: originId ?? undefined },
    });
  }

  /** Shipping behaviour fixed in the API's code — read-only. */
  public getPreferences(): Observable<ShippingPreferences> {
    return this.request({
      path: 'integrations/easyship/settings/preferences',
      method: 'GET',
    });
  }

  /** A trial's label or form as a file, fetched by the API from Easyship. */
  public downloadDocument(
    orderId: number,
    legId: number,
    kind: 'label' | 'return-label' | 'battery-form'
  ): Observable<Blob> {
    return this.request(
      { path: `integrations/easyship/orders/${orderId}/documents/${legId}/${kind}`, method: 'GET' },
      { responseType: 'blob' }
    );
  }
}
